import Features from "@app/feature-flags/features";
import Roles from "@app/user/roles";
import { ClientType } from "@app/client/client";
import ProjectOptions from "@app/projects/projectOptions";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { ProjectRunConfigurationStatus, ProjectRunStatusEnum } from "@app/projects/enumerators";
import { ProjectModule } from "@app/projects/literals";
import { isHistoricalRun } from "@app/projects/network-assessment/network-integrity/helpers/historicalRunHelper";
import { userCanAccessAIChatPanel } from "@app/projects/network-assessment/provider-hub/ai-assistant/aiChatPanelAuthorizationHelper";

/**
 * Three-state verdict for the project-level "Ask Quinn" button.
 *
 * `hidden` means the client or project does not have the capability at all; `disabled` means they do,
 * but Provider Hub — which is where Quinn's data comes from — cannot answer for the run being viewed.
 * Mirrors the unavailable / not-ready / ready split already used for Provider Hub deep links in
 * `network-integrity/helpers/providerHubLinkHelper.tsx`.
 */
/**
 * Why Provider Hub cannot answer for the run on screen. Surfaced so the UI can say which of the
 * conditions actually applies instead of listing all of them and making the user guess.
 */
export type AskQuinnDisabledReason =
  | "accuracyTab"
  | "projectProcessing"
  | "compareMode"
  | "timeSlice"
  | "providerHubIngesting";

/** Sentence fragments, completed into a full sentence by `getAskQuinnDisabledMessage`. */
export const askQuinnDisabledReasonText: Record<AskQuinnDisabledReason, string> = {
  accuracyTab: "Quinn does not support the Accuracy tab",
  projectProcessing: "the project is currently being processed",
  compareMode: "the project is currently in compare mode",
  timeSlice: "an unpublished, historical, or projected/termed time slice is currently being viewed",
  providerHubIngesting: "Provider Hub is ingesting new data",
};

/** The single sentence shown to users wherever Quinn explains why it is unavailable. */
export const getAskQuinnDisabledMessage = (reason: AskQuinnDisabledReason): string =>
  `The 'Ask Quinn' feature is currently disabled because ${askQuinnDisabledReasonText[reason]}.`;

export type AskQuinnButtonState =
  | { kind: "hidden" }
  | { kind: "disabled"; reason: AskQuinnDisabledReason }
  | { kind: "enabled" };

export interface AskQuinnButtonGateInput {
  enabledFeatureFlags: Set<string>;
  roles: string[];
  clientType?: ClientType;
  project?: Partial<ProjectOptions>;
  runConfiguration?: Partial<ProjectRunConfiguration>;
  isDualMembershipMode?: boolean;
  compareToRunConfiguration?: Partial<ProjectRunConfiguration>;
  activeProjectModule?: ProjectModule;
}

/** A run that has finished analyzing. FailedProviderChanges still has usable adequacy results. */
const isRunComplete = (status?: ProjectRunConfigurationStatus): boolean =>
  status === ProjectRunConfigurationStatus.Succeeded
  || status === ProjectRunConfigurationStatus.FailedProviderChanges;

/**
 * Quinn answers questions out of Provider Hub's Snowflake data, so the button is only offered to
 * clients who have both Adequacy and Provider Hub, and only enabled when Provider Hub itself would
 * be usable for the run on screen.
 */
export const getAskQuinnButtonState = ({
  enabledFeatureFlags,
  roles,
  clientType,
  project,
  runConfiguration,
  isDualMembershipMode,
  compareToRunConfiguration,
  activeProjectModule,
}: AskQuinnButtonGateInput): AskQuinnButtonState => {
  // --- Tier 1: is the capability present at all? ---

  if (!userCanAccessAIChatPanel(enabledFeatureFlags, roles)) {
    return { kind: "hidden" };
  }

  if (!enabledFeatureFlags.has(Features.AI_ADEQUACY_SUMMARY)) {
    return { kind: "hidden" };
  }

  // Regulator clients have neither Adequacy nor Provider Hub, so they would already fail the checks
  // below. Kept explicit so the exclusion does not silently depend on how flags happen to be provisioned.
  if (clientType === ClientType.Regulator || clientType === ClientType.TwoSidedRegulator) {
    return { kind: "hidden" };
  }

  // Truthy rather than != null, matching CountyProjectOverview's shouldShowAdequacyTab -- a
  // non-adequacy run carries an empty template id, not a null one.
  if (!runConfiguration?.adequacyTemplateId) {
    return { kind: "hidden" };
  }

  const hasProviderHub = enabledFeatureFlags.has(Features.PROVIDER_HUB)
    && (roles.includes(Roles.PROVIDER_HUB_PREVIEW) || enabledFeatureFlags.has(Features.PROVIDER_HUB_PUBLIC));
  if (!hasProviderHub) {
    return { kind: "hidden" };
  }

  // --- Tier 2: is Provider Hub usable for this particular run? ---

  // Checked in order, because more than one of these can be true at once and the UI reports a
  // single reason. The order follows the list the feature's copy is written from, so what a user
  // reads here matches the order they would read it on the disabled button.

  // Quinn only answers about adequacy. Reaching here means the run has an adequacy template, so an
  // accuracy template as well makes it blended, and its Accuracy tab is outside what Quinn knows.
  if (activeProjectModule === "Accuracy" && !!runConfiguration.accuracyTemplateId) {
    return { kind: "disabled", reason: "accuracyTab" };
  }

  // Finished analyzing, and the project is not mid-run.
  if (!isRunComplete(runConfiguration.status) || project?.isRunning) {
    return { kind: "disabled", reason: "projectProcessing" };
  }

  // Compare / dual-membership mode, which Provider Hub itself does not support.
  if (isDualMembershipMode || !!compareToRunConfiguration) {
    return { kind: "disabled", reason: "compareMode" };
  }

  // Published and still the latest -- Provider Hub's servicing data only ever describes the latest
  // published run, so a superseded one would be answered against the wrong slice -- and not a
  // simulated slice either.
  if (!runConfiguration.datePublished
    || isHistoricalRun(runConfiguration)
    || runConfiguration.isTermed
    || runConfiguration.isProjected
    || runConfiguration.isProjectedTermed) {
    return { kind: "disabled", reason: "timeSlice" };
  }

  // Servicing providers are ingested, so there is something to query. The API reports ingestion
  // status for the latest published run only, which the check above has established this to be.
  if (!runConfiguration.isServicingProviderInSnowflake
    || project?.servicingProviderIngestionStatus !== ProjectRunStatusEnum.Succeeded) {
    return { kind: "disabled", reason: "providerHubIngesting" };
  }

  return { kind: "enabled" };
};
